import { supabase } from '../lib/supabase.js';
import { getState, setState } from '../state/store.js';
import { openFormSheet, closeFormSheet } from '../ui/modals.js';
import { loadRecentTransactions } from '../transactions/transactions.js';
import { loadMaterialsForSite } from '../materials/materials.js';
import { loadLabourForSite } from '../labour/labour.js';
import { loadProjectHealth, loadDashboardTransactions } from '../dashboard/projectHealth.js';
import { isOnline, OFFLINE_SAVE_MESSAGE } from '../lib/onlineGuard.js';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'paused', label: 'Paused' }
];

export async function loadSites() {
  const { data, error } = await supabase
    .from('sites')
    .select('id, name, client_name, location, status, quotation_amount, created_at')
    .order('name', { ascending: true });

  if (error) {
    console.error('Failed to load sites', error);
    setState({ sites: [], sitesLoaded: true, sitesError: true });
    return;
  }

  const sites = data || [];
  const state = getState();

  // If a previously-selected site (from localStorage) no longer exists or is no
  // longer visible to this user, drop the selection rather than silently keep
  // pointing at a site the UI can't show data for.
  let selectedSiteId = state.selectedSiteId;
  if (selectedSiteId && !sites.some((s) => s.id === selectedSiteId)) {
    selectedSiteId = null;
  }

  setState({ sites, sitesLoaded: true, sitesError: false, selectedSiteId });
}

export function selectSite(siteId) {
  setState({ selectedSiteId: siteId });
  // Selected site propagates into every screen that reads from the store; what
  // needs an explicit re-fetch (not just a re-render) is whatever is fetched
  // per-site from Supabase -- transactions, materials, labour.
  loadRecentTransactions(siteId);
  loadMaterialsForSite(siteId);
  loadLabourForSite(siteId);
}

export function getSelectedSite() {
  const state = getState();
  return state.sites.find((s) => s.id === state.selectedSiteId) || null;
}

// RLS already restricts sites INSERT/UPDATE/DELETE to admins ("admin manage
// sites" policy) -- this mirrors that at the UI layer so staff never see
// controls that would just be rejected server-side.
export function isAdmin() {
  return getState().profile?.role === 'admin';
}

// Single source of truth for what makes a site record valid, used by both
// saveSite() and updateSite() before any network call. Trims strings so
// whitespace-only input (which passes native `required`) is still rejected.
function validateSiteFields(fields) {
  const errors = {};
  const name = (fields.name || '').trim();
  const clientName = (fields.client_name || '').trim();
  const location = (fields.location || '').trim();
  const status = fields.status;
  const quotationRaw = fields.quotation_amount;

  if (!name) errors.name = 'Site name is required';
  if (!clientName) errors.client_name = 'Client name is required';
  if (!location) errors.location = 'Location is required';
  if (!STATUS_OPTIONS.some((o) => o.value === status)) errors.status = 'Status is required';

  if (quotationRaw === '' || quotationRaw === null || quotationRaw === undefined) {
    errors.quotation_amount = 'Quotation amount is required';
  } else {
    const q = Number(quotationRaw);
    if (Number.isNaN(q)) errors.quotation_amount = 'Quotation amount must be a number';
    else if (q < 0) errors.quotation_amount = 'Quotation amount cannot be negative';
  }

  return errors;
}

function siteFieldsPayload(fields) {
  return {
    name: fields.name.trim(),
    client_name: fields.client_name.trim(),
    location: fields.location.trim(),
    status: fields.status,
    quotation_amount: Number(fields.quotation_amount)
  };
}

// public.sites has NO unique constraint on `name` at the database level, so
// Postgres never raises 23505 for a duplicate -- the error.code check below
// is dead code against the current schema (kept as defense-in-depth in case
// a constraint is added later). Since schema changes are out of scope here,
// duplicate names are prevented at the application layer instead: checked
// against the sites already loaded into state (case-insensitive, trimmed),
// excluding the record being edited.
function findDuplicateSiteName(name, excludeId) {
  const trimmed = name.trim().toLowerCase();
  return getState().sites.find((s) => s.id !== excludeId && s.name.trim().toLowerCase() === trimmed);
}

/**
 * Inserts a new row into the EXISTING public.sites table -- no schema/RLS
 * changes. Returns { ok: true, site } or { ok: false, errors: { field: msg } }.
 */
export async function saveSite(fields) {
  const errors = validateSiteFields(fields);
  if (!errors.name && findDuplicateSiteName(fields.name, null)) {
    errors.name = 'A site with this name already exists.';
  }
  if (Object.keys(errors).length) return { ok: false, errors };
  if (!isOnline()) return { ok: false, errors: { _general: OFFLINE_SAVE_MESSAGE } };

  const { data, error } = await supabase
    .from('sites')
    .insert(siteFieldsPayload(fields))
    .select('id, name, client_name, location, status, quotation_amount')
    .single();

  if (error) {
    if (error.code === '23505') {
      return { ok: false, errors: { name: 'A site with this name already exists.' } };
    }
    return { ok: false, errors: { _general: error.message } };
  }

  return { ok: true, site: data };
}

/**
 * Updates the existing row by id -- never inserts, so it can never create a
 * duplicate site. Returns the same shape as saveSite().
 */
export async function updateSite(siteId, fields) {
  const errors = validateSiteFields(fields);
  if (!errors.name && findDuplicateSiteName(fields.name, siteId)) {
    errors.name = 'A site with this name already exists.';
  }
  if (Object.keys(errors).length) return { ok: false, errors };
  if (!isOnline()) return { ok: false, errors: { _general: OFFLINE_SAVE_MESSAGE } };

  const { data, error } = await supabase
    .from('sites')
    .update(siteFieldsPayload(fields))
    .eq('id', siteId)
    .select('id, name, client_name, location, status, quotation_amount')
    .single();

  if (error) {
    if (error.code === '23505') {
      return { ok: false, errors: { name: 'A site with this name already exists.' } };
    }
    return { ok: false, errors: { _general: error.message } };
  }

  return { ok: true, site: data };
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (m) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
  }[m]));
}

function siteFormBodyHtml(values) {
  const v = values || {};
  const status = v.status || 'active';
  return `
    <label>Site Name
      <input name="name" value="${escapeHtml(v.name || '')}" placeholder="e.g. Kochi Marina Apartment" required maxlength="200">
      <small class="fieldError" data-error-for="name" hidden></small>
    </label>
    <label>Client Name
      <input name="client_name" value="${escapeHtml(v.client_name || '')}" placeholder="e.g. Mr. Rajesh Kumar" required maxlength="200">
      <small class="fieldError" data-error-for="client_name" hidden></small>
    </label>
    <label>Location
      <input name="location" value="${escapeHtml(v.location || '')}" placeholder="e.g. Kakkanad, Kochi" required maxlength="200">
      <small class="fieldError" data-error-for="location" hidden></small>
    </label>
    <label>Status
      <select name="status" required>
        ${STATUS_OPTIONS.map((o) => `<option value="${o.value}"${status === o.value ? ' selected' : ''}>${o.label}</option>`).join('')}
      </select>
      <small class="fieldError" data-error-for="status" hidden></small>
    </label>
    <label>Quotation Amount (₹)
      <input name="quotation_amount" type="number" min="0" step="0.01" value="${v.quotation_amount ?? ''}" required>
      <small class="fieldError" data-error-for="quotation_amount" hidden></small>
    </label>
    <div class="formError" id="siteFormGeneralError" hidden></div>
  `;
}

function readSiteFormFields(form) {
  const fd = new FormData(form);
  return {
    name: fd.get('name'),
    client_name: fd.get('client_name'),
    location: fd.get('location'),
    status: fd.get('status'),
    quotation_amount: fd.get('quotation_amount')
  };
}

function clearSiteFormErrors(form) {
  form.querySelectorAll('.fieldError').forEach((el) => {
    el.hidden = true;
    el.textContent = '';
  });
  form.querySelectorAll('input.fieldInvalid, select.fieldInvalid').forEach((el) => el.classList.remove('fieldInvalid'));
  const generalBox = form.querySelector('#siteFormGeneralError');
  if (generalBox) {
    generalBox.hidden = true;
    generalBox.textContent = '';
  }
}

function showSiteFormErrors(form, errors) {
  clearSiteFormErrors(form);
  for (const [field, msg] of Object.entries(errors)) {
    if (field === '_general') {
      const generalBox = form.querySelector('#siteFormGeneralError');
      generalBox.textContent = msg;
      generalBox.hidden = false;
      continue;
    }
    const errEl = form.querySelector(`[data-error-for="${field}"]`);
    const inputEl = form.querySelector(`[name="${field}"]`);
    if (errEl) {
      errEl.textContent = msg;
      errEl.hidden = false;
    }
    if (inputEl) inputEl.classList.add('fieldInvalid');
  }
}

/**
 * Shared Add/Edit Site form. `onSubmit` is saveSite or updateSite (bound to
 * the target id); `onSuccess` refreshes whatever screens need the new data.
 * A single `submitting` flag (checked before any async work starts, and also
 * expressed as a disabled submit button) is what prevents duplicate/double
 * submissions -- not a debounce or a client-side dedupe of site names.
 */
function openSiteForm({ title, initialValues, submitLabel, onSubmit, onSuccess }) {
  const body = document.createElement('form');
  body.className = 'sheetForm siteForm';
  body.innerHTML = `
    ${siteFormBodyHtml(initialValues)}
    <div class="siteFormActions">
      <button type="button" class="dBtn light" data-cancel>Cancel</button>
      <button class="wideBtn" type="submit">${submitLabel}</button>
    </div>
  `;

  let submitting = false;
  const submitBtn = body.querySelector('button[type="submit"]');
  const cancelBtn = body.querySelector('[data-cancel]');

  // Clear a field's error the moment the user edits it, instead of leaving a
  // stale message up after they've already fixed the value.
  body.querySelectorAll('input[name], select[name]').forEach((el) => {
    el.addEventListener('input', () => {
      el.classList.remove('fieldInvalid');
      const errEl = body.querySelector(`[data-error-for="${el.name}"]`);
      if (errEl) {
        errEl.hidden = true;
        errEl.textContent = '';
      }
    });
  });

  cancelBtn.addEventListener('click', () => closeFormSheet());

  body.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (submitting) return; // guards against double-tap / double-submit
    submitting = true;
    clearSiteFormErrors(body);
    submitBtn.disabled = true;
    cancelBtn.disabled = true;
    submitBtn.textContent = 'Saving…';

    const fields = readSiteFormFields(body);
    const result = await onSubmit(fields);

    if (!result.ok) {
      showSiteFormErrors(body, result.errors || { _general: 'Something went wrong. Please try again.' });
      submitting = false;
      submitBtn.disabled = false;
      cancelBtn.disabled = false;
      submitBtn.textContent = submitLabel;
      return; // do NOT show success, do NOT close, until Supabase confirms
    }

    closeFormSheet();
    await onSuccess(result.site);
  });

  openFormSheet(title, body);
}

export function openAddSiteForm() {
  openSiteForm({
    title: 'Add Site',
    initialValues: { status: 'active' },
    submitLabel: 'Save Site',
    onSubmit: (fields) => saveSite(fields),
    onSuccess: async (site) => {
      await loadSites();
      selectSite(site.id);
      await Promise.all([loadProjectHealth(), loadDashboardTransactions()]);
    }
  });
}

export function openEditSiteForm(site) {
  openSiteForm({
    title: 'Edit Site',
    initialValues: site,
    submitLabel: 'Save Changes',
    onSubmit: (fields) => updateSite(site.id, fields),
    onSuccess: async () => {
      // Re-fetch rather than patch state locally -- keeps sites.js the single
      // data source every screen (Dashboard, Project Health, site selector,
      // Reports, P&L) reads from, with no second copy of site data to drift.
      await loadSites();
      await Promise.all([loadProjectHealth(), loadDashboardTransactions()]);
    }
  });
}

function statusPillHtml(status) {
  const label = STATUS_OPTIONS.find((o) => o.value === status)?.label || status || 'Active';
  const pillClass = status === 'completed' ? 'blue' : status === 'paused' ? 'amber' : 'green';
  return `<span class="statusPill ${pillClass}">${escapeHtml(label)}</span>`;
}

export function openSitePicker() {
  const state = getState();
  const admin = isAdmin();
  const body = document.createElement('div');
  body.className = 'sitePickerList';

  const rows = !state.sites.length
    ? '<div class="empty">No sites available yet.</div>'
    : state.sites.map((s) => `
      <div class="sitePickerRow${s.id === state.selectedSiteId ? ' selected' : ''}">
        <button type="button" class="sitePickerRowMain" data-site-id="${s.id}">
          <span>
            <b>${escapeHtml(s.name)}</b>
            <small>${escapeHtml(s.client_name || '')}${s.location ? ' · ' + escapeHtml(s.location) : ''}</small>
          </span>
          <span class="sitePickerRowMeta">
            ${statusPillHtml(s.status)}
            ${s.id === state.selectedSiteId ? '<span class="checkmark">✓</span>' : ''}
          </span>
        </button>
        ${admin ? `<button type="button" class="sitePickerEditBtn" data-edit-site-id="${s.id}" aria-label="Edit ${escapeHtml(s.name)}">✎</button>` : ''}
      </div>
    `).join('');

  body.innerHTML = admin
    ? `${rows}<button type="button" class="wideBtn" id="openAddSiteBtn">+ Add Site</button>`
    : rows;

  body.querySelectorAll('.sitePickerRowMain').forEach((row) => {
    row.addEventListener('click', () => {
      selectSite(row.dataset.siteId);
      closeFormSheet();
    });
  });

  body.querySelectorAll('.sitePickerEditBtn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const site = state.sites.find((s) => s.id === btn.dataset.editSiteId);
      if (site) openEditSiteForm(site);
    });
  });

  body.querySelector('#openAddSiteBtn')?.addEventListener('click', () => {
    openAddSiteForm();
  });

  openFormSheet('Select Site', body);
}
